/**
 * Submit hook for `CaseManagementInteractionForm`. Thin wrapper around
 * `useEncounterUpsertSubmit` with the `case-management-interaction`
 * discriminator.
 *
 * @agentic connections
 */

import type { Encounter, Patient, PractitionerRole } from "@medplum/fhirtypes";
import type {
  CaseManagementInteractionFormData,
  EncounterActivityContext,
} from "@benchmark/core/types";

import { useEncounterUpsertSubmit } from "./useEncounterUpsertSubmit";

/**
 * Options for {@link useCaseManagementInteractionSubmit}.
 *
 * @agentic connections
 */
export type UseCaseManagementInteractionSubmitOptions = {
  patient: Patient | undefined;
  existingEncounter?: Encounter;
  practitionerRoles?: PractitionerRole[];
  activityContext?: EncounterActivityContext;
  onSuccess?: (encounterId: string) => void;
  onSubmitted?: () => void;
  getCareTimeMinutes?: () => number | undefined;
  getCareTimeCommunicationId?: () => string | undefined;
  getCareTimeEffectivePeriod?: () => { start: string; end: string } | undefined;
};

/**
 * Returns a submit handler for `CaseManagementInteractionForm`.
 *
 * @agentic connections
 */
export const useCaseManagementInteractionSubmit = (
  options: UseCaseManagementInteractionSubmitOptions,
): ((formData: CaseManagementInteractionFormData) => Promise<void>) =>
  useEncounterUpsertSubmit<"case-management-interaction">({
    type: "case-management-interaction",
    careTimeMethod: "task-completion",
    ...options,
  });
